import { useEffect, useRef, useState } from "react";
import { Send, PhoneOff, Loader2, Mic, Bot, User, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, vapiEnabled, VAPI_PUBLIC_KEY, VAPI_ASSISTANT_ID } from "@/lib/api";

const sessionId = () =>
  (window.crypto?.randomUUID?.() || `sess-${Date.now()}-${Math.random()}`);

let _msgSeq = 0;
const mk = (role, text) => ({ id: `m${++_msgSeq}`, role, text });

const SUGGESTIONS = [
  "I'd like to book a haircut.",
  "I'd like to order a pizza.",
  "I need to book a dentist appointment.",
  "I'd like to arrange a property viewing.",
  "Tell me about OperaMind.",
  "Can you speak Spanish?",
  "Can you help me in French?",
  "مرحباً",
];

const DEFAULT_GREETING =
  "Hello and welcome to OperaMind. My name is Olivia, OperaMind's AI Receptionist demonstration. Pretend you're calling any business and I'll respond as that business's receptionist.";

export default function DemoWidget() {
  const [mode, setMode] = useState("chat");
  const [session] = useState(sessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // voice state
  const vapiRef = useRef(null);
  const [callState, setCallState] = useState("idle"); // idle | connecting | active
  const [speaking, setSpeaking] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [voiceErr, setVoiceErr] = useState("");

  useEffect(() => {
    api.get("/demo/config").then((r) => {
      setMessages([mk("assistant", r.data?.greeting || DEFAULT_GREETING)]);
    }).catch(() => setMessages([mk("assistant", DEFAULT_GREETING)]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, busy]);

  const sendMessage = async (text) => {
    const t = (text || "").trim();
    if (!t || busy) return;
    setInput("");
    setMessages((m) => [...m, mk("user", t)]);
    setBusy(true);
    try {
      const r = await api.post("/demo/chat", { session_id: session, message: t });
      setMessages((m) => [...m, mk("assistant", r.data.reply)]);
    } catch (e) {
      const msg = e.response?.status === 429
        ? (e.response?.data?.detail || "You've reached the demo limit. Start your free trial to keep chatting.")
        : "Sorry, I had trouble connecting. Please try again.";
      setMessages((m) => [...m, mk("assistant", msg)]);
    } finally {
      setBusy(false);
    }
  };

  const startVoice = async () => {
    try {
      setVoiceErr("");
      setCallState("connecting");
      try {
        await api.post("/demo/voice/session");
      } catch (e) {
        setCallState("idle");
        setVoiceErr(e.response?.status === 429
          ? (e.response?.data?.detail || "You've reached the demo limit. Please try again later.")
          : "Couldn't start the call. Please try again.");
        return;
      }
      const { default: Vapi } = await import("@vapi-ai/web");
      const vapi = new Vapi(VAPI_PUBLIC_KEY);
      vapiRef.current = vapi;
      vapi.on("call-start", () => setCallState("active"));
      vapi.on("call-end", () => { setCallState("idle"); setSpeaking(false); setThinking(false); });
      vapi.on("speech-start", () => { setSpeaking(true); setThinking(false); });
      vapi.on("speech-end", () => setSpeaking(false));
      vapi.on("error", (e) => {
        setCallState("idle"); setSpeaking(false); setThinking(false);
        setVoiceErr(e?.errorMsg || e?.error?.message || "Couldn't start the call. Check your Vapi assistant & credits.");
      });
      vapi.on("message", (msg) => {
        if (msg.type === "transcript" && msg.transcriptType === "final") {
          const role = msg.role === "assistant" ? "assistant" : "user";
          if (role === "user") setThinking(true);
          setMessages((m) => [...m, mk(role, msg.transcript)]);
        }
      });
      vapi.start(VAPI_ASSISTANT_ID);
    } catch (e) {
      setCallState("idle");
      setVoiceErr("Couldn't connect to the voice service. Please try again.");
    }
  };

  const stopVoice = () => {
    vapiRef.current?.stop();
    setCallState("idle");
  };

  const voiceStatus = () => {
    if (callState === "idle") return "Tap to start a live voice call with Olivia";
    if (callState === "connecting") return "Connecting…";
    if (speaking) return "Speaking…";
    if (thinking) return "Thinking…";
    return "Listening…";
  };

  return (
    <div
      data-testid="demo-widget"
      className="w-full rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden flex flex-col"
      style={{ height: 560 }}
    >
      {/* header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="relative h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center text-white">
            <Bot className="h-5 w-5" />
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-slate-50" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900 leading-tight">Olivia · AI Receptionist</p>
            <p className="text-xs text-emerald-600 font-medium flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live AI Demonstration
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-slate-200/70 p-0.5">
          <button
            data-testid="demo-mode-chat"
            onClick={() => setMode("chat")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${mode === "chat" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
          >
            Chat
          </button>
          <button
            data-testid="demo-mode-voice"
            onClick={() => setMode("voice")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${mode === "voice" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
          >
            Voice
          </button>
        </div>
      </div>

      {mode === "voice" ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6 text-center">
          <div className="relative">
            <div className={`relative h-28 w-28 rounded-full flex items-center justify-center ${callState === "active" ? "voice-ripple" : ""}`}>
              <div className={`h-24 w-24 rounded-full flex items-center justify-center transition-colors ${callState === "active" ? "bg-blue-600" : "bg-slate-900"}`}>
                {callState === "connecting" ? (
                  <Loader2 className="h-9 w-9 text-white animate-spin" />
                ) : (
                  <Mic className={`h-9 w-9 text-white ${speaking ? "scale-110" : ""} transition-transform`} />
                )}
              </div>
            </div>
          </div>
          <div>
            <p data-testid="voice-status" className="text-sm font-semibold text-slate-900">{voiceStatus()}</p>
            {voiceErr && <p data-testid="voice-error" className="text-xs text-red-600 mt-1 max-w-[260px]">{voiceErr}</p>}
            <p className="text-xs text-slate-500 mt-1 max-w-[260px]">
              {vapiEnabled
                ? "Speak naturally — pretend you're calling any business and Olivia will respond as that business's receptionist."
                : "Voice calling activates once Vapi credentials are added. Try Chat mode for now."}
            </p>
          </div>
          {callState === "idle" ? (
            <Button
              data-testid="start-voice-btn"
              disabled={!vapiEnabled}
              onClick={startVoice}
              className="rounded-full bg-blue-600 hover:bg-blue-700 gap-2 px-6"
            >
              <PhoneCall className="h-4 w-4" /> Start voice call
            </Button>
          ) : (
            <Button data-testid="stop-voice-btn" onClick={stopVoice} variant="destructive" className="rounded-full gap-2 px-6">
              <PhoneOff className="h-4 w-4" /> End call
            </Button>
          )}
        </div>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white">
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="h-7 w-7 shrink-0 rounded-full bg-blue-600 flex items-center justify-center text-white">
                    <Bot className="h-4 w-4" />
                  </div>
                )}
                <div
                  className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-slate-900 text-white rounded-br-sm"
                      : "bg-slate-100 text-slate-800 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
                {m.role === "user" && (
                  <div className="h-7 w-7 shrink-0 rounded-full bg-slate-200 flex items-center justify-center text-slate-600">
                    <User className="h-4 w-4" />
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex gap-2 justify-start">
                <div className="h-7 w-7 shrink-0 rounded-full bg-blue-600 flex items-center justify-center text-white">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
                  <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* suggested prompts */}
          <div className="px-3 pt-2 flex gap-2 overflow-x-auto no-scrollbar border-t border-slate-100">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid="demo-suggestion"
                onClick={() => sendMessage(s)}
                disabled={busy}
                className="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600 transition-colors disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>

          <div className="p-3 bg-white">
            <div className="flex items-center gap-2">
              <Input
                data-testid="demo-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
                placeholder="Pretend you're calling any business…"
                className="rounded-full border-slate-200"
              />
              <Button
                data-testid="demo-chat-send"
                onClick={() => sendMessage(input)}
                disabled={busy}
                size="icon"
                className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
